class ClayBrandHero extends HTMLElement {
  #index = 0;
  #timer = null;
  #pointerStartX = null;
  #pointerId = null;
  #didDrag = false;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-slide-dot]')];

    if (!this.slides.length) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('dragstart', this.#preventNativeDrag);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#show(0);
    this.#resume();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('dragstart', this.#preventNativeDrag);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.#pause();
  }

  get autoplay() {
    return this.dataset.autoplay === 'true';
  }

  get delay() {
    const seconds = Number(this.dataset.autoplaySeconds || 6);
    return Math.max(3000, seconds * 1000);
  }

  #show(index) {
    if (!this.slides.length) return;

    this.#index = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.#index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  #next = () => {
    this.#show(this.#index + 1);
  };

  #previous = () => {
    this.#show(this.#index - 1);
  };

  #handleClick = (event) => {
    // Links/buttons never enter drag mode, so they must never be cancelled by
    // the drag guard. Only suppress a residual click on a non-interactive area.
    if (
      this.#didDrag &&
      !event.target.closest('a, button, input, select, textarea, label, [role="button"], [role="link"]')
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.#didDrag = false;
      return;
    }

    const dot = event.target.closest('[data-slide-dot]');

    if (dot) {
      this.#show(Number(dot.dataset.slideDot || 0));
      this.#restart();
      return;
    }

    if (event.target.closest('[data-slide-next]')) {
      this.#next();
      this.#restart();
      return;
    }

    if (event.target.closest('[data-slide-prev]')) {
      this.#previous();
      this.#restart();
    }
  };

  #handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    // Never start a drag from an interactive control. In particular, links
    // must keep their native pointer/click sequence without pointer capture.
    if (
      event.target.closest(
        'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [data-no-drag]'
      )
    ) {
      return;
    }

    this.#pointerStartX = event.clientX;
    this.#pointerId = event.pointerId;
    this.#didDrag = false;

    this.setPointerCapture?.(event.pointerId);
    this.classList.add('is-dragging');
    this.#pause();
  };

  #handlePointerMove = (event) => {
    if (this.#pointerStartX === null || event.pointerId !== this.#pointerId) return;

    const delta = event.clientX - this.#pointerStartX;

    if (Math.abs(delta) > 8) {
      this.#didDrag = true;
    }
  };

  #handlePointerUp = (event) => {
    if (this.#pointerStartX === null || event.pointerId !== this.#pointerId) return;

    const delta = event.clientX - this.#pointerStartX;

    this.releasePointerCapture?.(event.pointerId);
    this.classList.remove('is-dragging');

    this.#pointerStartX = null;
    this.#pointerId = null;

    if (Math.abs(delta) >= 45) {
      this.#didDrag = true;

      if (delta < 0) {
        this.#next();
      } else {
        this.#previous();
      }
    }

    this.#restart();

    if (this.#didDrag) {
      window.setTimeout(() => {
        this.#didDrag = false;
      }, 0);
    }
  };

  #handlePointerCancel = (event) => {
    if (this.#pointerId !== null && event.pointerId === this.#pointerId) {
      this.releasePointerCapture?.(event.pointerId);
    }

    this.classList.remove('is-dragging');
    this.#pointerStartX = null;
    this.#pointerId = null;
    this.#didDrag = false;
    this.#resume();
  };

  #preventNativeDrag = (event) => {
    event.preventDefault();
  };

  #pause = () => {
    clearInterval(this.#timer);
    this.#timer = null;
  };

  #resume = () => {
    if (!this.autoplay || this.slides.length <= 1) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#pause();
    this.#timer = window.setInterval(this.#next, this.delay);
  };

  #restart() {
    this.#pause();
    this.#resume();
  }
}

if (!customElements.get('clay-brand-hero')) {
  customElements.define('clay-brand-hero', ClayBrandHero);
}
